import {
  darkModeColors,
  ElementsEnvironmentContextProvider,
  ElementsUIContextProvider,
  LocalThemeContextProvider,
} from '@elements-financial/elements';
import { Outlet, useSearchParams } from '@remix-run/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTypedLoaderData } from 'remix-typedjson';
import { RootDocument } from './components';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type rootLoader } from './loaders';
import Cookies from 'universal-cookie';
import { useEffect } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      // Disable cache for now until we fully migrate to react-query
      cacheTime: 0,
    },
  },
});

export const RootPage = () => {
  const { isDev, apiUrl } = useTypedLoaderData<typeof rootLoader>();
  const [searchParams] = useSearchParams();

  const accessToken = searchParams.get('accessToken')!;
  const refreshToken = searchParams.get('refreshToken')!;
  const householdID = searchParams.get('householdID');

  useEffect(() => {
    const cookies = new Cookies();
      cookies.set(
        '__accessToken',
        btoa(JSON.stringify({ access_token: accessToken })),
        {
          path: '/',
          sameSite: 'lax',
          httpOnly: false,
          secure: process.env.NODE_ENV === 'production',
        }
      );
      cookies.set(
        '__refreshToken',
        btoa(JSON.stringify({ refresh_token: refreshToken, })),
        {
          path: '/',
          sameSite: 'lax',
          httpOnly: false,
          secure: process.env.NODE_ENV === 'production',
        }
      );
  }, [accessToken, refreshToken]);

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files

  return (
    <RootDocument>
      <ElementsEnvironmentContextProvider
        app={'client'}
        isDev={isDev}
        apiUrl={apiUrl}
        authentication={{
          accessToken: accessToken,
          refreshToken: refreshToken,
        }}
        householdID={householdID}
      >
        <LocalThemeContextProvider paletteSeedColor={darkModeColors.accent}>
          <ElementsUIContextProvider>
            <QueryClientProvider client={queryClient}>
              <Outlet />
              <ReactQueryDevtools position="bottom-right" initialIsOpen={false} />
            </QueryClientProvider>
          </ElementsUIContextProvider>
        </LocalThemeContextProvider>
      </ElementsEnvironmentContextProvider>
    </RootDocument>
  );
};
